'use client';

import { useState, useEffect } from 'react';
import { detectRegion, saveConsent, getConsent, logConsentAction } from '@/lib/consent';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';

export default function CookieBanner() {
  const [region, setRegion] = useState<'gdpr' | 'ccpa' | 'none'>('none');
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const detected = detectRegion();
    setRegion(detected);

    if (detected !== 'none') {
      const existing = getConsent();
      if (!existing) {
        setVisible(true);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    saveConsent(
      {
        necessary: true,
        analytics: true,
        marketing: true,
        preferences: true,
      },
      region
    );
    logConsentAction('accept_all', { region });
    setVisible(false);
  };

  const handleRejectAll = () => {
    saveConsent(
      {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
      },
      region
    );
    logConsentAction('reject_all', { region });
    setVisible(false);
  };

  const handleCCPADismiss = () => {
    saveConsent(
      {
        necessary: true,
        analytics: true,
        marketing: false,
        preferences: true,
      },
      region
    );
    logConsentAction('ccpa_dismiss', { region });
    setVisible(false);
  };

  if (!visible || region === 'none') return null;

  return (
    <>
      {region === 'gdpr' && (
        <div
          role="dialog"
          aria-label="Cookie consent"
          aria-live="polite"
          className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
        >
          <div className="mx-auto max-w-4xl bg-white border border-gray-200 rounded-lg shadow-lg p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Cookie Consent</h2>
              <p className="text-sm text-gray-600 mb-3">
                We use cookies to enhance your experience and analyze site usage. Your privacy is important to us.
              </p>
              <p className="text-sm text-gray-600">
                Learn more about our data practices in our{' '}
                <a href="/privacy" className="text-blue-600 hover:underline">
                  Privacy Policy
                </a>
                {' '}and{' '}
                <a href="/cookies" className="text-blue-600 hover:underline">
                  Cookie Policy
                </a>
                .
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Reject All
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Manage Preferences
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}

      {region === 'ccpa' && (
        <div
          role="dialog"
          aria-label="Cookie notice"
          aria-live="polite"
          className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
        >
          <div className="mx-auto max-w-4xl bg-white border border-gray-200 rounded-lg shadow-lg p-6">
            <p className="text-sm text-gray-600 mb-4">
              We use cookies and similar technologies to enhance your experience. You have the right to opt out of the sale of your personal information. See our{' '}
              <a href="/privacy#do-not-sell" className="text-blue-600 hover:underline">
                Do Not Sell My Personal Information
              </a>
              {' '}link for more details.
            </p>
            <button
              onClick={handleCCPADismiss}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <ConsentPreferenceCenter
        open={showPreferences}
        onClose={() => setShowPreferences(false)}
        region={region}
      />
    </>
  );
}
